import { Link } from "react-router-dom";
import { ArrowRight, FileText, Layers } from "lucide-react";
import { ILLUSTRATIVE_DISCLAIMER, ILLUSTRATIVE_SCENARIOS, SCENARIO_FOOTNOTE } from "../data/illustrative_scenarios";
import { getSample } from "../data/sample_deliverables";
import { ResultsDisclaimer } from "./ResultsDisclaimer";

export function IllustrativeBadge({ className = "" }) {
  return (
    <span className={`inline-flex items-center border border-amber-400/40 bg-amber-400/[0.06] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-200 ${className}`} data-testid="illustrative-badge">
      Illustrative scenario · not a testimonial
    </span>
  );
}

export function ScenarioCard({ s, expanded = false }) {
  return (
    <article className="flex h-full flex-col border border-zinc-800 bg-[#0a070d] p-6 transition-colors hover:border-purple-500/50 sm:p-7" data-testid={`scenario-card-${s.id}`}>
      <IllustrativeBadge />
      <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-purple-300">{s.category}</div>
      <h3 className="mt-2 font-display text-2xl font-black leading-tight text-white">{s.name} — {s.role}</h3>
      <p className="mt-1 text-xs text-zinc-500">Fictional first name used for illustration.</p>
      <p className="mt-4 text-sm leading-relaxed text-zinc-300">{s.before}</p>
      <dl className="mt-5 space-y-3 text-sm">
        <div><dt className="text-zinc-500">Track selected</dt><dd><Link to={`/p/${s.trackSlug}`} className="text-purple-300 hover:text-purple-200" data-testid={`scenario-track-${s.id}`}>{s.trackName}</Link></dd></div>
        <div><dt className="text-zinc-500">Stages followed</dt><dd className="text-zinc-300">{s.stages.join(" → ")}</dd></div>
        <div><dt className="text-zinc-500">Asset created</dt><dd className="text-zinc-100">{s.asset}</dd></div>
        <div><dt className="text-zinc-500">Example route to market</dt><dd className="text-zinc-300">{s.routeToMarket}</dd></div>
      </dl>
      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Example deliverables</div>
        <ul className="mt-2 grid gap-1.5 text-sm text-zinc-300 sm:grid-cols-2">
          {s.deliverables.map((d) => <li key={d} className="flex gap-2"><span className="text-purple-400">·</span>{d}</li>)}
        </ul>
      </div>
      <p className="mt-4 text-sm text-amber-100/90">{s.suggestedOffer}</p>
      <p className="mt-2 text-sm text-zinc-400"><span className="text-zinc-500">Realistic next step:</span> {s.nextStep}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {s.sampleIds.map((id) => {
          const smp = getSample(id);
          return smp ? (
            <Link key={id} to={`/sample-outputs/${id}`} className="inline-flex min-h-11 items-center gap-2 border border-zinc-700 px-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-purple-400" data-testid={`scenario-sample-${id}`}>
              <FileText size={14} aria-hidden="true" /> {smp.type}
            </Link>
          ) : null;
        })}
      </div>
      <p className="mt-5 border-t border-zinc-800 pt-4 text-xs leading-relaxed text-zinc-500" data-testid={`scenario-disclaimer-${s.id}`}>{ILLUSTRATIVE_DISCLAIMER} {expanded && SCENARIO_FOOTNOTE}</p>
    </article>
  );
}

export function WhatCouldYouBuild({ limit = 6 }) {
  return (
    <section className="border-b border-white/10 bg-[#050308]" data-testid="what-could-you-build">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:px-10 lg:py-28">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-purple-300"><Layers size={14} aria-hidden="true" /> Ideas built with Stag Vault</div>
          <h2 className="mt-4 font-display text-3xl font-black leading-[1.05] text-white sm:text-4xl lg:text-5xl">What Could You Build With Stag Vault?</h2>
          <p className="mt-5 text-base leading-relaxed text-zinc-300 md:text-lg">Stag Vault is designed to move you from an idea to a finished commercial asset. These illustrative scenarios show what different users could create by following a track from beginning to end.</p>
          <p className="mt-3 text-sm text-amber-200/90">Every scenario below is a fictional, clearly labelled example — not a customer story. Verified member stories will appear <Link to="/member-stories" className="underline underline-offset-4 hover:text-white">here</Link> only with permission and evidence.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {ILLUSTRATIVE_SCENARIOS.slice(0, limit).map((s) => <ScenarioCard key={s.id} s={s} />)}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-5">
          <Link to="/sample-outputs" className="inline-flex min-h-11 items-center gap-2 border border-purple-400 px-5 text-xs font-black uppercase tracking-[0.15em] text-white transition-colors hover:bg-purple-500 hover:text-black" data-testid="scenarios-sample-outputs-link">See the sample deliverables <ArrowRight size={14} aria-hidden="true" /></Link>
          <p className="text-xs text-zinc-500">{SCENARIO_FOOTNOTE}</p>
        </div>
        <ResultsDisclaimer className="mt-10" testId="scenarios-results-disclaimer" />
      </div>
    </section>
  );
}
